"use strict";
//
// Copyright © 2025 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
Object.defineProperty(exports, "__esModule", { value: true });
exports.FibonacciDelayStrategy = exports.ExponentialBackoffStrategy = exports.FixedDelayStrategy = exports.DelayStrategyFactory = void 0;
exports.sleep = sleep;
exports.DelayStrategyFactory = {
    /**
     * Create a fixed delay strategy
     */
    fixed(options) {
        return new FixedDelayStrategy(options);
    },
    /**
     * Create an exponential backoff delay strategy
     */
    exponentialBackoff(options) {
        return new ExponentialBackoffStrategy(options);
    },
    /**
     * Create a Fibonacci delay strategy
     */
    fibonacci(options) {
        return new FibonacciDelayStrategy(options);
    }
};
/**
 * Fixed delay strategy - uses the same delay for all retry attempts
 */
class FixedDelayStrategy {
    delayMs;
    jitter;
    constructor(options) {
        this.delayMs = options.delayMs;
        this.jitter = options.jitter ?? 0;
    }
    getDelay(_attempt) {
        if (this.jitter > 0) {
            const jitterAmount = this.delayMs * this.jitter * (Math.random() * 2 - 1);
            return Math.max(0, this.delayMs + jitterAmount);
        }
        return this.delayMs;
    }
}
exports.FixedDelayStrategy = FixedDelayStrategy;
/**
 * Exponential backoff delay strategy - increases delay exponentially with each attempt
 */
class ExponentialBackoffStrategy {
    initialDelayMs;
    maxDelayMs;
    backoffFactor;
    jitter;
    constructor(options) {
        this.initialDelayMs = options.initialDelayMs;
        this.maxDelayMs = options.maxDelayMs;
        this.backoffFactor = options.backoffFactor;
        this.jitter = options.jitter ?? 0;
    }
    getDelay(attempt) {
        const baseDelay = Math.min(this.initialDelayMs * Math.pow(this.backoffFactor, attempt - 1), this.maxDelayMs);
        if (this.jitter > 0) {
            const jitterAmount = baseDelay * this.jitter * (Math.random() * 2 - 1);
            return Math.min(Math.max(0, baseDelay + jitterAmount), this.maxDelayMs);
        }
        return baseDelay;
    }
}
exports.ExponentialBackoffStrategy = ExponentialBackoffStrategy;
class FibonacciDelayStrategy {
    baseDelayMs;
    maxDelayMs;
    jitter;
    // Cache for Fibonacci numbers to improve performance
    fibCache;
    constructor(options) {
        this.baseDelayMs = options.baseDelayMs;
        this.maxDelayMs = options.maxDelayMs;
        this.jitter = options.jitter ?? 0;
        this.fibCache = new Map([
            [0, 0],
            [1, 1]
        ]);
    }
    fibonacci(n) {
        // Return from cache if available
        if (this.fibCache.has(n)) {
            return this.fibCache.get(n);
        }
        if (n <= 1) {
            return n;
        }
        // Calculate using recursion with memoization
        const result = this.fibonacci(n - 1) + this.fibonacci(n - 2);
        this.fibCache.set(n, result);
        return result;
    }
    getDelay(attempt) {
        const fibNumber = this.fibonacci(attempt + 1);
        const baseDelay = Math.min(fibNumber * this.baseDelayMs, this.maxDelayMs);
        if (this.jitter > 0) {
            const jitterAmount = baseDelay * this.jitter * (Math.random() * 2 - 1);
            return Math.min(Math.max(0, baseDelay + jitterAmount), this.maxDelayMs);
        }
        return baseDelay;
    }
}
exports.FibonacciDelayStrategy = FibonacciDelayStrategy;
/**
 * Promise-based sleep function
 */
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
//# sourceMappingURL=delay.js.map